/**
 * Deferred PWA install prompt (task 5.3b).
 *
 * Captures the browser's `beforeinstallprompt`, calls `preventDefault()` so
 * the browser does NOT show its own mini-infobar, and holds the event until
 * the app decides the moment is right. Without that `preventDefault()` the
 * whole exercise is moot: the browser prompts on its own schedule and the
 * deferred event can no longer be used.
 *
 * Eligibility state lives in `services/installPromptStore`; this hook is the
 * React-facing half that turns it into a rendering decision.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  VISITS_BEFORE_PROMPT,
  getInstallVisitCount,
  isInstallPromptSuppressed,
  markInstalled,
  recordQualifyingVisit,
  suppressInstallPrompt,
} from '../services/installPromptStore';

// `beforeinstallprompt` is not part of TypeScript's DOM lib (it is a
// nonstandard, Chromium-only event) -- this is the real shape the browser
// dispatches, typed locally rather than reached for with a cast to `any`.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/**
 * THE TRIGGER (task 5.3b step 2).
 *
 * The prompt is offered on the SECOND session in which the app has actually
 * shown this person their own real Canvas data -- not the first, and never
 * in demo or guest mode.
 *
 * "Not on first load" is the floor the plan states, not the goal. Coming
 * back is the cheapest honest evidence that the app was worth coming back
 * to; a first visit proves only curiosity, and a parent evaluating this
 * during a school run is exactly the person an install banner would annoy.
 * Loading real data is required as well as returning, because a visit that
 * ended in a spinner or an error is not value received -- prompting there
 * would make "install this" the first thing a broken app asks for.
 *
 * Demo and guest are evaluation modes by construction (AppModeContext's
 * `coursesMode`), so a caller must never pass `hasReceivedValue` for them:
 * asking someone to install an app they have not yet decided to use inverts
 * the order of the decision.
 *
 * @param hasReceivedValue whether this visit has shown the user their own
 *   real data, and so counts towards the trigger
 */
export function useInstallPrompt(hasReceivedValue: boolean) {
  // The event itself is a ref, not state: it is a single-use handle the
  // browser hands over, and re-rendering because we are holding one is not
  // the same question as whether we are allowed to use it.
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [hasDeferredPrompt, setHasDeferredPrompt] = useState(false);
  const [visits, setVisits] = useState(getInstallVisitCount);
  const [suppressed, setSuppressed] = useState(isInstallPromptSuppressed);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      // Required. Without it the browser shows its own mini-infobar and the
      // deferred event cannot be replayed later.
      event.preventDefault();
      deferredRef.current = event as BeforeInstallPromptEvent;
      setHasDeferredPrompt(true);
    };

    const handleAppInstalled = () => {
      deferredRef.current = null;
      setHasDeferredPrompt(false);
      markInstalled();
      setSuppressed(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Counted at most once per browser session -- see recordQualifyingVisit.
  useEffect(() => {
    if (!hasReceivedValue) return;
    setVisits(recordQualifyingVisit());
  }, [hasReceivedValue]);

  const dismiss = useCallback(() => {
    suppressInstallPrompt();
    setSuppressed(true);
  }, []);

  const promptInstall = useCallback(async (): Promise<string | null> => {
    const deferred = deferredRef.current;
    if (!deferred) return null;

    // Single-use: once prompt() has been called the browser will not accept
    // the same event again, so drop our handle before awaiting rather than
    // after, where a second click could slip past.
    deferredRef.current = null;

    let outcome: string | null = null;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      outcome = choice ? choice.outcome : null;
    } catch (error) {
      console.error('Install prompt failed:', error);
    }

    // Declining the browser's own dialog is as much a "no" as our "Not now"
    // button, and earns the same suppression window. Deciding this only
    // after userChoice resolves is the point: a banner that hid itself the
    // moment it was clicked would be guessing at an answer the user has not
    // given yet.
    if (outcome === 'dismissed') {
      suppressInstallPrompt();
      setSuppressed(true);
    }

    setHasDeferredPrompt(false);
    return outcome;
  }, []);

  const canPrompt = hasDeferredPrompt && !suppressed && visits >= VISITS_BEFORE_PROMPT;

  return { canPrompt, promptInstall, dismiss };
}

export default useInstallPrompt;
